import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    Button,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Modal,
    CategoryForm,
    MessageModal
} from 'common/components';
import {
    ApiStatus,
    ButtonLabel,
    ButtonVariant,
    CategoryField,
    EntityType,
    MessageType
} from 'common/enums';
import {
    ACTION_ITEMS,
    CLONE_CONFIRMATION_TITLE,
    CLONE_CONFIRMATION_DESCRIPTION,
    FAILURE_HEADER,
    CLONE_DISABLED
} from 'common/config';
import { EntityVM } from 'common/models/data/entity.model';
import { removeValidationError } from 'common/utils/error';
import {
    validateCategoryDescription,
    validateCategoryForm,
    validateCategoryName,
    validateCategorySortOrder
} from 'common/utils/validator';
import { RootState } from 'common/redux/core/root.reducer';

import { masterDataActions } from 'modules/master-data/redux';
import { getCloneFeasibility, getFieldErrors } from 'modules/master-data/redux/masterData.selector';

import './CategoryClone.scss';
import { appStateActions } from 'common/redux/app-state';

type CategoryCloneProps = {
    data: EntityVM;
    onClose: () => void;
};

const CategoryClone: FC<CategoryCloneProps> = ({ data, onClose }) => {
    const [name, setName] = useState<string>(data?.name ? data.name : '');
    const [sortOrder, setSortOrder] = useState<string>(data?.order ? data.order : '');
    const [description, setDescription] = useState<string>(
        data?.description ? data.description : ''
    );
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const fieldErrors = useSelector((state: RootState) => getFieldErrors(state));
    const cloneFeasibility = useSelector((state: RootState) => getCloneFeasibility(state));

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(
            masterDataActions.getCloneFeasibility({
                id: data.id,
                entityType: data.entityType
            })
        );
    }, [data.entityType, data.id, dispatch]);

    useEffect(() => {
        setName(data?.name ? data.name : '');
        setSortOrder(data?.order ? data.order : '');
        setDescription(data?.description ? data.description : '');
    }, [data]);

    useEffect(() => {
        if (cloneFeasibility.status === ApiStatus.SUCCESS) {
            if (cloneFeasibility.data) {
                setIsModalVisible(true);
            } else {
                dispatch(
                    appStateActions.showMessageModal({
                        isVisible: true,
                        header: FAILURE_HEADER,
                        message: CLONE_DISABLED,
                        type: MessageType.Error
                    })
                );
                dispatch(masterDataActions.resetCloneFeasibility());
                onClose();
            }
        } else if (cloneFeasibility.status === ApiStatus.ERROR) {
            dispatch(masterDataActions.resetCloneFeasibility());
            onClose();
        }
    }, [cloneFeasibility, dispatch, onClose]);

    const handleSubmit = () => {
        let errors = fieldErrors;
        errors = validateCategoryForm(name, sortOrder, errors);
        if (errors.length === 0) {
            setIsConfirmationVisible(true);
            setIsModalVisible(false);
        }
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleDescription = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Description);
        errors = validateCategoryDescription(event.target.value, errors);
        setDescription(event.target.value);
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleNameChange = (event: any) => {
        let errors = removeValidationError(fieldErrors, CategoryField.Name);
        errors = validateCategoryName(event.target.value, errors);
        setName(event.target.value);
        dispatch(masterDataActions.setFieldErrors(errors));
    };

    const handleSortOrderChange = (value: string) => {
        let errors = removeValidationError(fieldErrors, CategoryField.SortOrder);
        errors = validateCategorySortOrder(value, errors);
        dispatch(masterDataActions.setFieldErrors(errors));
        setSortOrder(value);
    };

    const handleClose = () => {
        setName('');
        setSortOrder('');
        setDescription('');
        setIsConfirmationVisible(false);
        onClose();
        dispatch(masterDataActions.setFieldErrors([]));
    };

    const handleConfirmationClose = () => {
        setIsConfirmationVisible(false);
        onClose();
    };

    const handleConfirmation = () => {
        const newData = {
            name: name,
            order: sortOrder,
            description: description,
            sourceCategoryId: data.id,
            targetCategoryId: null,
            targetSubCategoryId: null,
            entityType: EntityType.Category
        };
        dispatch(masterDataActions.cloneEntity(newData));
        handleConfirmationClose();
    };

    return (
        <>
            <Modal
                className="category-clone-modal"
                show={isModalVisible}
                style={{ width: '51vw' }}
                onClose={onClose}>
                <ModalHeader className="category-clone-header">Clone a category</ModalHeader>
                <ModalBody className="category-clone-body">
                    <CategoryForm
                        name={name}
                        sortOrder={sortOrder}
                        description={description}
                        fieldErrors={fieldErrors}
                        handleNameChange={handleNameChange}
                        handleDescription={handleDescription}
                        handleSortOrderChange={handleSortOrderChange}
                    />
                </ModalBody>
                <ModalFooter className="category-clone-footer">
                    <Button
                        variant={ButtonVariant.Primary}
                        disabled={fieldErrors.length > 0}
                        onClick={handleSubmit}>
                        {ACTION_ITEMS.SUBMIT_BUTTON}
                    </Button>
                    <Button variant={ButtonVariant.Secondary} onClick={handleClose}>
                        {ACTION_ITEMS.CANCEL_BUTTON}
                    </Button>
                </ModalFooter>
            </Modal>
            {isConfirmationVisible && (
                <MessageModal
                    headerName={CLONE_CONFIRMATION_TITLE}
                    message={CLONE_CONFIRMATION_DESCRIPTION}
                    isVisible={isConfirmationVisible}
                    isSecondaryButtonVisible={true}
                    primaryButton={ButtonLabel.Ok}
                    secondaryButton={ButtonLabel.Cancel}
                    onPrimaryAction={handleConfirmation}
                    onSecondaryAction={handleConfirmationClose}
                />
            )}
        </>
    );
};

export default CategoryClone;
